/**
 * FilterBar — top-of-table or top-of-list filter chrome with three slots:
 * search (start), filter chips (middle), and actions (end).
 *
 * Use when: a screen has a search field plus tag filters above a table.
 * For just toolbar buttons use `AppToolbar`. For form fieldsets use
 * `FormSection`.
 */
import type { ReactNode } from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    flexWrap: 'wrap',
  },
  start: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  middle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flex: 1,
    minWidth: 0,
    flexWrap: 'wrap',
  },
  end: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
  },
});

interface FilterBarProps {
  /** Left slot — typically the search input. */
  search?: ReactNode;
  /** Middle slot — filter chips (Badges with appearance="tint"). */
  filters?: ReactNode;
  /** Right slot — actions (Add, Refresh, Export). */
  actions?: ReactNode;
}

export function FilterBar({ search, filters, actions }: FilterBarProps) {
  const styles = useStyles();
  return (
    <div className={styles.root}>
      {search ? <div className={styles.start}>{search}</div> : null}
      {filters ? <div className={styles.middle}>{filters}</div> : null}
      {actions ? <div className={styles.end}>{actions}</div> : null}
    </div>
  );
}
